import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, formatApiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ShieldCheck, Ban, Play, Crown, AlertTriangle } from 'lucide-react';

const ROLES = ['user', 'owner', 'admin', 'super_admin'];

export default function AdminUsers() {
  const [sp] = useSearchParams();
  const { user: me } = useAuth();
  const isSuper = me?.role === 'super_admin';
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [role, setRole] = useState(sp.get('role') || 'all');
  const [verification, setVerification] = useState(sp.get('verification') || 'all');
  const [suspended, setSuspended] = useState(sp.get('suspended') === 'true' ? 'true' : 'all');
  const [dialog, setDialog] = useState(null); // {kind, user}
  const [reason, setReason] = useState('');
  const [newRole, setNewRole] = useState('user');

  const load = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (role !== 'all') params.role = role;
    if (verification !== 'all') params.verification = verification;
    if (suspended !== 'all') params.suspended = suspended === 'true';
    const { data } = await api.get('/admin/users', { params });
    setItems(data || []);
  }, [q, role, verification, suspended]);
  useEffect(() => { load(); }, [load]);

  const doAction = async () => {
    if (!dialog) return;
    const { kind, user } = dialog;
    try {
      if (kind === 'suspend') await api.post(`/admin/users/${user.user_id}/suspend`, { reason });
      else if (kind === 'reactivate') await api.post(`/admin/users/${user.user_id}/reactivate`, { reason });
      else if (kind === 'verify') await api.post(`/admin/users/${user.user_id}/verify`, { reason });
      else if (kind === 'unverify') await api.post(`/admin/users/${user.user_id}/unverify`, { reason });
      else if (kind === 'role') await api.post(`/admin/users/${user.user_id}/role`, { role: newRole, reason });
      toast.success('Operazione completata');
      setDialog(null); setReason(''); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta">Utenti</div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tighter mt-2">Gestione utenti</h1>
        <p className="mt-2 text-sm text-muted-foreground">Solo un SUPER_ADMIN può modificare ruoli o gestire altri ADMIN. Le protezioni sono applicate lato backend.</p>
      </header>

      <div className="rounded-2xl border border-border bg-card p-4 flex flex-wrap gap-3">
        <Input placeholder="Cerca nome o email" value={q} onChange={e=>setQ(e.target.value)} className="max-w-xs" data-testid="admin-users-search"/>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-[150px]"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i ruoli</SelectItem>
            {ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={verification} onValueChange={setVerification}>
          <SelectTrigger className="w-[180px]"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Ogni stato verifica</SelectItem>
            <SelectItem value="unverified">Non verificato</SelectItem>
            <SelectItem value="pending">Verifica in corso</SelectItem>
            <SelectItem value="verified">Verificato</SelectItem>
          </SelectContent>
        </Select>
        <Select value={suspended} onValueChange={setSuspended}>
          <SelectTrigger className="w-[150px]"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli account</SelectItem>
            <SelectItem value="false">Attivi</SelectItem>
            <SelectItem value="true">Sospesi</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        {items.length===0 ? <div className="p-10 text-sm text-muted-foreground text-center">Nessun utente</div> :
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/60">
                <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <th className="px-4 py-3">Utente</th>
                  <th className="px-4 py-3">Ruolo</th>
                  <th className="px-4 py-3">Verifica</th>
                  <th className="px-4 py-3">Stato</th>
                  <th className="px-4 py-3">Terreni</th>
                  <th className="px-4 py-3">Iscritto</th>
                  <th className="px-4 py-3">Ultimo accesso</th>
                  <th className="px-4 py-3 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map(u => {
                  const canManageAdmin = u.role === 'super_admin' ? false : (u.role === 'admin' ? isSuper : true);
                  return (
                    <tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium flex items-center gap-1">
                          {u.role==='super_admin' && <Crown size={12} className="text-terracotta"/>}
                          {u.name}
                        </div>
                        <div className="text-xs text-muted-foreground">{u.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-secondary">{u.role}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${u.verification_status==='verified'?'bg-accent text-accent-foreground':u.verification_status==='pending'?'bg-terracotta/20 text-terracotta':'bg-secondary'}`}>
                          {u.verification_status || 'unverified'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {u.suspended ? <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-destructive/20 text-destructive">sospeso</span> : <span className="text-xs text-muted-foreground">attivo</span>}
                      </td>
                      <td className="px-4 py-3">{u.listings_count || 0}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(u.created_at).toLocaleDateString('it-IT')}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{u.last_login_at ? new Date(u.last_login_at).toLocaleDateString('it-IT') : '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 justify-end flex-wrap">
                          {u.verification_status !== 'verified' && canManageAdmin && (
                            <Button size="sm" variant="outline" onClick={()=>setDialog({kind:'verify', user:u})}><ShieldCheck size={12} className="mr-1"/> Verifica</Button>
                          )}
                          {isSuper && u.user_id !== me.user_id && (
                            <Button size="sm" variant="outline" onClick={()=>{ setNewRole(u.role); setDialog({kind:'role', user:u}); }}><Crown size={12} className="mr-1"/> Ruolo</Button>
                          )}
                          {canManageAdmin && !u.suspended && (
                            <Button size="sm" variant="outline" onClick={()=>setDialog({kind:'suspend', user:u})}><Ban size={12} className="mr-1"/> Sospendi</Button>
                          )}
                          {canManageAdmin && u.suspended && (
                            <Button size="sm" variant="outline" onClick={()=>setDialog({kind:'reactivate', user:u})}><Play size={12} className="mr-1"/> Riattiva</Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>}
      </div>

      <Dialog open={!!dialog} onOpenChange={o=>{ if(!o){setDialog(null); setReason('');} }}>
        <DialogContent>
          <DialogHeader><DialogTitle>
            {dialog?.kind==='role' ? 'Modifica ruolo' : dialog?.kind==='suspend' ? 'Sospendi utente' : dialog?.kind==='reactivate' ? 'Riattiva utente' : dialog?.kind==='verify' ? 'Verifica utente' : 'Rimuovi verifica'}
          </DialogTitle></DialogHeader>
          <div className="text-sm text-muted-foreground">Utente: <b>{dialog?.user?.email}</b></div>
          {dialog?.kind === 'role' && (
            <>
              <div className="text-sm mt-2">Nuovo ruolo:</div>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              {newRole === 'super_admin' && (
                <div className="rounded-lg bg-terracotta/10 text-terracotta text-xs p-3 flex gap-2">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5"/> Assegnare SUPER_ADMIN concede pieno controllo sulla piattaforma. Confermare con attenzione.
                </div>
              )}
            </>
          )}
          <Textarea rows={3} placeholder="Motivo (registrato in audit log)" value={reason} onChange={e=>setReason(e.target.value)}/>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>{setDialog(null); setReason('');}}>Annulla</Button>
            <Button onClick={doAction} data-testid="admin-confirm-action">Conferma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
